import { useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../../common/hooks/use-is-component-mounted";
import useOrderOptimizerList from "../../../../common/services/order-optimizer/list/use-order-optimizer-list";
import useAbort from "../../../../common/hooks/use-abort";
import useOrderOptimizerTaxiListingFiltersDao from "./filters/use-order-optimizer-taxi-listing-filters.dao";
import OrderOptimizerTaxiListingFilter from "./common/types/order-optimizer-taxi-listing-filter";
import OrderOptimizerTaxiListingFilterType from "./common/types/order-optimizer-taxi-listing-filter-type";
import OrderOptimizerListParams from "../../../../common/services/order-optimizer/list/order-optimizer-list-params";
import orderOptimizerTaxiListingLoadParamsFactory from "./common/order-optimizer-taxi-listing-load-params.factory";
import usePagination from "../../../../common/hooks/use-pagination";
import orderOptimizerTranslationsHelper from "../../../../languages/order-optimizer-translations.helper";
import { useAppContext } from "../../../../context/app.context";
import orderOptimizerBreadcrumbsHelper from "../../common/breadcrumbs/order-optimizer-breadcrumbs.helper";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import orderOptimizerTaxiListingItemFactory from "./common/order-optimizer-taxi-listing-item.factory";
import { OrderOptimizerTaxiListingItem } from "./common/types/order-optimizer-taxi-listing-item";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import OrderOptimizerTaxiListingFiltersComponent from "./filters/order-optimizer-taxi-listing-filters.component";
import OrderOptimizerTaxiListingTableComponent from "./table/order-optimizer-taxi-listing-table.component";
import CardComponent from "../../../../common/components/card/card.component";
import ListingPaginationComponent from "../../../../common/components/listing/pagination/listing-pagination.component";
import StatusLegendComponent from "../../../../common/components/status-legend/status-legend.component";
import orderOptimzierTaxiListingHelper from "./common/order-optimizer-taxi-listing.helper";

const OrderOptimizerTaxiListingComponent = () => {
  const isComponentMounted = useIsComponentMounted();

  const orderOptimizerList = useOrderOptimizerList();
  const orderOptimizerListAbort = useAbort();

  const filtersDao = useOrderOptimizerTaxiListingFiltersDao();

  const [filters, setFilters] = useState<OrderOptimizerTaxiListingFilter[]>(
    () => filtersDao.getFilters(),
  );

  const selectedHumanId = useMemo(() => {
    const filterValue = filters.find(
      (filter) =>
        filter.type === OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID,
    )?.value as string | undefined;

    return Number.parseInt(filterValue ?? "");
  }, [filters]);

  const loadListingItems = () => {
    const params: OrderOptimizerListParams =
      orderOptimizerTaxiListingLoadParamsFactory.create(
        filters,
        page,
        pageSize,
      );

    orderOptimizerList.load(params, orderOptimizerListAbort.signal);
  };

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: orderOptimizerList.data.totalCount,
    defaultPageSize: filtersDao.getPageSize() ?? 10, // limit set to 10 (default 100)
    defaultPage: filtersDao.getPage(),
  });

  const saveFilters = () => {
    filtersDao.saveFilters(filters, page, pageSize);
  };

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    saveFilters();
  }, [filters, page, pageSize, isComponentMounted]);

  useEffect(() => {
    loadListingItems();

    return () => orderOptimizerListAbort.revoke();
  }, [page, pageSize, filters]);

  const translations =
    orderOptimizerTranslationsHelper.getTaxiListingTranslations();
  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();

  useEffect(() => {
    const breadcrumbs = orderOptimizerBreadcrumbsHelper.getListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.orderOptimizerListing);

  const listingItems: OrderOptimizerTaxiListingItem[] = useMemo(() => {
    return orderOptimizerTaxiListingItemFactory.createListingItems(
      orderOptimizerList.data.data,
    );
  }, [orderOptimizerList.data]);

  const statusOptions = orderOptimzierTaxiListingHelper.getStatusOptions();

  return (
    <>
      <HeadingComponent title={translations.header.headingLabel} />
      <StatusLegendComponent statusData={statusOptions} />
      <OrderOptimizerTaxiListingFiltersComponent
        filters={filters}
        isListingLoading={orderOptimizerList.isLoading}
        onFiltersChange={(filters) => {
          setFilters(filters);
          setPage(1);
        }}
      />
      <CardComponent>
        <OrderOptimizerTaxiListingTableComponent
          listingItems={listingItems}
          selectedHumanId={selectedHumanId}
          isError={orderOptimizerList.isError}
          isLoading={orderOptimizerList.isLoading}
          applyOptimizationStatus={orderOptimizerList.applyOptimizationStatus}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={orderOptimizerList.data.totalCount}
        />
      </CardComponent>
    </>
  );
};

export default OrderOptimizerTaxiListingComponent;
